import { useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import { Check, ChevronDown, ChevronRight, Plus } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { restoreItem } from '../lib/db'
import { useQueryClient } from '@tanstack/react-query'
import { Banner, Button, Checkbox, Field, Progress, Row, Sheet, Tag, type SwipeAction, useToast } from '../ui'
import {
  useAddItem, useNewRun, useRemoveItem, useSetItemDone, useUpdateItem,
} from '../lib/mutations'
import { useFeed, useListRuns } from '../lib/queries'
import { attempt, useWriteError } from '../lib/useAction'
import { heroIsVenue, heroUrl, posterUrl } from '../lib/entries'
import { leadingOf, metaLine, plateFor, rowTitle } from '../lib/rowMeta'
import { EventSheet } from './EventSheet'
import type { FeedRow, List, ListItem } from '../lib/types'
import './EventRow.css'
import { lastRunOf, shortDate } from '../lib/listSummary'
import { RowMore } from './RowMore'


/**
 * L5: a template, run. Split out of `ListDetail.tsx` by WP 4.5 the same way
 * WP 4.4 split `GroceryList` and `TaskList` — the ring, the run history and
 * the room collapse are enough of a shape of their own that folding them back
 * into `ListDetail`'s `if`s would recreate the file WP 4.4's own note already
 * warned about.
 */
export function Checklist({ list, items }: { list: List; items: ListItem[] }) {
  const toast = useToast()
  const qc = useQueryClient()
  const [templateAdd, setTemplateAdd] = useState(false)
  const [edit, setEdit] = useState<ListItem | null>(null)
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  // L8: which event's sheet is open, from tapping an item that carries an
  // `entry_id` — the item editor is not reached from that tap.
  const [openEntry, setOpenEntry] = useState<FeedRow | null>(null)

  const addM = useAddItem()
  const toggleM = useSetItemDone()
  const removeM = useRemoveItem()
  const updateM = useUpdateItem()
  const newRunM = useNewRun()
  const runsQ = useListRuns()
  const error = useWriteError(addM, toggleM, removeM, updateM, newRunM)
  const busy = addM.isPending || newRunM.isPending

  const feedQ = useFeed()
  const feedById = useMemo(() => new Map((feedQ.data ?? []).map(e => [e.id, e])), [feedQ.data])
  const entryFor = (i: ListItem): FeedRow | null => (i.entry_id ? feedById.get(i.entry_id) ?? null : null)

  const done = items.filter(i => i.done_at)
  const runs = (runsQ.data ?? []).filter(r => r.list_id === list.id)
  const lastRun = lastRunOf(runs)
  const lastRunLabel = lastRun
    ? `Last run ${shortDate(lastRun.completed_at)} · ${runs.length} run${runs.length === 1 ? '' : 's'}`
    : 'No runs yet'
  const pct = items.length ? Math.round((done.length / items.length) * 100) : 0

  const sections = [...new Set(items.map(i => i.section ?? 'Other'))]

  async function add(body: string, section?: string | null) {
    if (!body.trim()) return
    await attempt(addM.mutateAsync({
      listId: list.id, body: body.trim(), opts: { section, position: items.length },
    }))
  }

  const toggle = (i: ListItem) => toggleM.mutate({ id: i.id, done: !i.done_at })

  // F7: removal is a swipe and a toast, never a confirmation dialog — the same
  // shape `ListDetail.tsx`'s own `restore` was before this split.
  async function restore(i: ListItem, gone: Promise<unknown>) {
    const { error: err } = await restoreItem(i, gone)
    if (!err) void qc.invalidateQueries({ queryKey: ['list_items'] })
  }

  const del = (i: ListItem) => {
    // The delete's own promise, so Undo writes the row back after it and
    // not before (a2-14): the row returns under its own id now, which a
    // delete still in flight would otherwise remove a second time.
    const gone = removeM.mutateAsync(i.id).catch(() => {})
    toast({ text: `Removed "${i.body}"`, action: { label: 'Undo', onClick: () => void restore(i, gone) } })
  }

  /** L5's own entry point, distinct from a template's "Start fresh"
   *  (`resetList`, `db.ts`'s own note on the two): logs a `list_runs` row,
   *  clears every tick, and hands the Undo enough to reverse both — every
   *  previously-done item ticked again, and the run row deleted outright
   *  rather than left behind as a run nothing actually finished. */
  async function startNewRun() {
    const wasDone = items.filter(i => i.done_at).map(i => i.id)
    let runId: string | null = null
    try {
      const res = await newRunM.mutateAsync(list.id)
      runId = res.data?.id ?? null
    } catch { return }
    toast({
      text: 'New run started',
      action: {
        label: 'Undo',
        onClick: () => {
          for (const id of wasDone) toggleM.mutate({ id, done: true })
          if (runId) {
            void supabase.from('list_runs').delete().eq('id', runId)
              .then(() => qc.invalidateQueries({ queryKey: ['list_runs'] }))
          }
        },
      },
    })
  }

  return (
    <>
      {error && <Banner tone="bad">{error}</Banner>}

      <div className="checklist__head">
        <Ring done={done.length} total={items.length} />
        <div className="checklist__headtext">
          <span className="checklist__count"><b>{done.length}</b> of {items.length} done</span>
          <span className="checklist__lastrun">{lastRunLabel}</span>
        </div>
      </div>

      <div className="stickybar">
        <span className="barbtns">
          <Button size="sm" variant="quiet" disabled={busy} onClick={() => void startNewRun()}>
            New run
          </Button>
          <Button size="sm" variant="secondary" icon={<Plus size={16} aria-hidden="true" />}
            onClick={() => setTemplateAdd(true)}>Add</Button>
        </span>
      </div>
      <Progress value={pct} label={`${done.length} of ${items.length} done`} />

      {sections.map(sec => {
        const mine = items.filter(i => (i.section ?? 'Other') === sec)
        const secDone = mine.filter(i => i.done_at).length
        const complete = mine.length > 0 && secDone === mine.length
        const isCollapsed = collapsed[sec] ?? complete
        return (
          <div key={sec}>
            <button
              type="button"
              className="checklist__sechead"
              aria-expanded={!isCollapsed}
              onClick={() => setCollapsed(c => ({ ...c, [sec]: !isCollapsed }))}
            >
              {complete
                ? <Check size={16} aria-hidden="true" className="checklist__sectick" />
                : (isCollapsed ? <ChevronRight size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />)}
              <span>{sec}</span>
              <span className="g">{secDone}/{mine.length}</span>
            </button>
            {!isCollapsed && mine.map(i => (
              <ChecklistRow key={i.id} item={i} entry={entryFor(i)} onOpenEvent={setOpenEntry}
                onToggle={() => toggle(i)} onDelete={() => del(i)} onOpen={() => setEdit(i)} />
            ))}
          </div>
        )
      })}

      {templateAdd && (
        <TemplateAddForm
          sections={sections}
          onClose={() => setTemplateAdd(false)}
          onSave={async (section, body) => { await add(body, section); setTemplateAdd(false) }}
        />
      )}
      {edit && (
        <ChecklistItemForm item={edit} onClose={() => setEdit(null)}
          onSave={body => { const id = edit.id; setEdit(null); updateM.mutate({ id, patch: { body } }) }} />
      )}

      <EventSheet e={openEntry} onClose={() => setOpenEntry(null)} />
    </>
  )
}

/** The 44px leading-slot ring, copied from `Lists.tsx`'s own (private, so not
 *  importable) — this is the same shape drawn at the same 44px, just in the
 *  header line instead of a row's leading slot. */
function Ring({ done, total }: { done: number; total: number }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0
  return (
    <span className="checklist__ring">
      <span
        className="lists-ring"
        role="img"
        aria-label={`${done} of ${total} done`}
        style={{ '--pct': `${pct}%` } as CSSProperties}
      />
    </span>
  )
}

function ChecklistRow({ item, entry, onOpenEvent, onToggle, onDelete, onOpen }: {
  item: ListItem
  /** L8: the feed row this item was added from, or null once it has left the
   *  feed — see `TaskList.tsx`'s own note on the same field. */
  entry?: FeedRow | null
  onOpenEvent?: (e: FeedRow) => void
  onToggle: () => void; onDelete: () => void; onOpen: () => void
}) {
  const checkbox = (
    <Checkbox
      size="lg"
      shape="square"
      checked={!!item.done_at}
      onChange={onToggle}
      aria-label={item.done_at ? `Mark "${item.body}" not done` : `Mark "${item.body}" done`}
    />
  )
  const swipe: { left: SwipeAction } = { left: { label: 'Remove', onAction: onDelete, leaves: true } }
  // a2-32: the swipe's Remove, and Edit, behind a "…" you can see. An event
  // item has no Edit — its tap opens the event.
  const more = (canEdit: boolean) => <RowMore name={item.body} onEdit={canEdit ? onOpen : undefined} onRemove={onDelete} />

  // L8: see `TaskList.tsx`'s own note on the same shape.
  if (item.entry_id) {
    if (entry) return <EventItemRow entry={entry} trailing={<>{checkbox}{more(false)}</>} swipe={swipe} onOpen={() => onOpenEvent?.(entry)} />
    return (
      <Row
        className="u-row--list"
        leading={checkbox}
        title={<span className="lists-itembody lists-itembody--wrap">{item.body}</span>}
        meta={<Tag>Passed</Tag>}
        trailing={more(false)}
        swipe={swipe}
      />
    )
  }

  return (
    <Row
      className="u-row--list"
      leading={checkbox}
      title={
        <button type="button" className={`lists-itembody lists-itembody--wrap${item.done_at ? ' is-done' : ''}`} onClick={onOpen}>
          {item.body}
        </button>
      }
      meta={null}
      trailing={more(true)}
      swipe={swipe}
    />
  )
}

function ChecklistItemForm({ item, onClose, onSave }: {
  item: ListItem; onClose: () => void; onSave: (body: string) => void
}) {
  const [body, setBody] = useState(item.body)
  return (
    <Sheet open onOpenChange={o => { if (!o) onClose() }} title="Edit item"
      actions={
        <>
          <Button variant="primary" disabled={!body.trim()} onClick={() => onSave(body.trim())}>Save</Button>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
        </>
      }>
      <Field label="Item"><Field.Input value={body} autoFocus onChange={e => setBody(e.target.value)} /></Field>
    </Sheet>
  )
}

function TemplateAddForm({ sections, onClose, onSave }: {
  sections: string[]; onClose: () => void; onSave: (section: string, body: string) => void
}) {
  const [section, setSection] = useState(sections[0] ?? '')
  const [fresh, setFresh] = useState('')
  const [body, setBody] = useState('')
  const target = section === '__new' ? fresh.trim() : section
  return (
    <Sheet open onOpenChange={o => { if (!o) onClose() }} title="Add to the checklist"
      actions={
        <>
          <Button variant="primary" disabled={!body.trim() || !target} onClick={() => onSave(target, body.trim())}>Add</Button>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
        </>
      }>
      <div className="lists-form">
        <p className="lists-backbar__label">Added to the checklist, so it comes back every run.</p>
        <Field label="Room">
          <Field.Select value={section} onChange={setSection}
            options={[...sections.map(s => ({ value: s, label: s })), { value: '__new', label: 'New room…' }]} />
        </Field>
        {section === '__new' && (
          <Field label="New room name">
            <Field.Input value={fresh} placeholder="Hallway" onChange={e => setFresh(e.target.value)} />
          </Field>
        )}
        <Field label="Task">
          <Field.Input value={body} placeholder="Hide the good towels" onChange={e => setBody(e.target.value)} />
        </Field>
      </div>
    </Sheet>
  )
}

// ---------------------------------------------------------------------------
// L8: an event, inside a list — the same shape `TaskList.tsx`'s own copy is,
// for the same reason (`Thumb`'s own note there).
// ---------------------------------------------------------------------------

function Thumb({ e }: { e: FeedRow }) {
  const now = new Date()
  const kind = leadingOf(e, { url: heroUrl(e, 112), isVenue: heroIsVenue(e), poster: posterUrl(e, 'w185'), now })
  const plate = kind.kind === 'poster' ? null : plateFor(e, now)
  return (
    <span className={`ev-lead ev-lead--${kind.kind}`}
      style={plate ? ({ '--ev-hue': plate.hue } as CSSProperties) : undefined}>
      {plate && (
        <span className="ev-plate">
          <b>{plate.big}</b>
          {plate.small && <i>{plate.small}</i>}
        </span>
      )}
      {kind.kind !== 'plate' && (
        <img src={kind.url} alt="" width={56} height={56} loading="lazy" decoding="async" />
      )}
    </span>
  )
}

function EventItemRow({ entry, trailing, swipe, onOpen }: {
  entry: FeedRow
  trailing: ReactNode
  swipe?: { left?: SwipeAction; right?: SwipeAction }
  onOpen: () => void
}) {
  const now = new Date()
  const m = metaLine(entry, { now })
  const rest = [m.venue, m.fact].filter(Boolean).join(' · ')
  return (
    <Row
      leading={<Thumb e={entry} />}
      title={rowTitle(entry)}
      meta={
        <>
          <span className="ev-row__dot" style={{ background: m.dot }} aria-hidden="true" />
          <b className="ev-row__when">{m.when}</b>
          {rest && <span className="ev-row__rest">{rest}</span>}
        </>
      }
      trailing={trailing}
      onPress={onOpen}
      swipe={swipe}
    />
  )
}
